/**
 * DevExtreme (esm/__internal/grids/data_grid/keyboard_navigation/m_group_panel_keyboard_navigation.js)
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import { name as clickEventName } from "../../../../common/core/events/click";
import eventsEngine from "../../../../common/core/events/core/events_engine";
import { isCommandKeyPressed } from "../../../../common/core/events/utils/index";
import $ from "../../../../core/renderer";
import { hiddenFocus } from "../../../../ui/shared/accessibility";
import { Direction } from "../../../grids/grid_core/keyboard_navigation/const";
import { ColumnKeyboardNavigationController } from "../../../grids/grid_core/keyboard_navigation/m_column_keyboard_navigation_core";
import { CLASSES as GROUPING_CLASSES } from "../grouping/const";
import gridCore from "../m_core";
import { ColumnKeyboardNavigationMixin } from "./m_column_keyboard_navigation_mixin";
export class GroupPanelKeyboardNavigationController extends ColumnKeyboardNavigationMixin(ColumnKeyboardNavigationController) {
  constructor() {
    super(...arguments);
    this.isNeedToHiddenFocusAfterClick = false;
  }
  groupItemClickHandler(e) {
    var _this$_columnsControl;
    const $groupedColumnElement = $(e.originalEvent.target);
    const groupColumn = this._columnsController.columnOption(`groupIndex:${$groupedColumnElement.index()}`);
    this.isNeedToHiddenFocusAfterClick = null === (_this$_columnsControl = this._columnsController) || void 0 === _this$_columnsControl ? void 0 : _this$_columnsControl.allowColumnSorting(groupColumn);
  }
  unsubscribeFromGroupItemClick() {
    const $focusedView = this.getFocusedViewElement();
    if ($focusedView) {
      eventsEngine.off($focusedView, clickEventName, this.groupItemClickHandlerContext);
    }
  }
  subscribeToGroupItemClick() {
    const $focusedView = this.getFocusedViewElement();
    if ($focusedView) {
      eventsEngine.on($focusedView, clickEventName, `.${GROUPING_CLASSES.groupPanelItem}`, this.groupItemClickHandlerContext);
    }
  }
  leftRightKeysHandler(e) {
    const {
      originalEvent: originalEvent
    } = e;
    if (isCommandKeyPressed(originalEvent)) {
      const $groupedColumnElement = $(originalEvent.target);
      const column = this._columnsController.columnOption(`groupIndex:${$groupedColumnElement.index()}`);
      const direction = this.getDirectionByKeyName(e.keyName);
      if (this.canReorderColumn(column, direction)) {
        this.moveColumn(column, direction);
      }
      null === originalEvent || void 0 === originalEvent || originalEvent.preventDefault();
    }
  }
  getVisibleIndex(column) {
    return column.groupIndex;
  }
  getColumnFromEvent(e) {
    const $groupedColumnElement = $(e.originalEvent.target);
    return this._columnsController.columnOption(`groupIndex:${$groupedColumnElement.index()}`);
  }
  getNewFocusedColumnBeforeUngrouping(column) {
    const visibleColumnIndex = column.groupIndex;
    const groupColumns = this._columnsController.getGroupColumns();
    return visibleColumnIndex === groupColumns.length - 1 ? groupColumns[visibleColumnIndex - 1] : groupColumns[visibleColumnIndex + 1];
  }
  _getCell(cellPosition) {
    var _this$headerPanel;
    const $groupColumnElements = null === (_this$headerPanel = this.headerPanel) || void 0 === _this$headerPanel ? void 0 : _this$headerPanel.getColumnElements();
    return null === $groupColumnElements || void 0 === $groupColumnElements ? void 0 : $groupColumnElements.eq(cellPosition.columnIndex);
  }
  getFocusedView() {
    return this.getView("headerPanel");
  }
  getFocusedViewElement() {
    var _this$headerPanel2;
    return null === (_this$headerPanel2 = this.headerPanel) || void 0 === _this$headerPanel2 || null === (_this$headerPanel2 = _this$headerPanel2.element()) || void 0 === _this$headerPanel2 ? void 0 : _this$headerPanel2.find(`.${GROUPING_CLASSES.groupPanel}`);
  }
  getFocusinSelector() {
    return `.${GROUPING_CLASSES.groupPanelItem}`;
  }
  focusinHandler(e) {
    this.setFocusedCellPosition(0, $(e.target).index());
  }
  keyDownHandler(e) {
    let isHandled = super.keyDownHandler(e);
    if (isHandled) {
      return true;
    }
    if ("leftArrow" === e.keyName || "rightArrow" === e.keyName) {
      this.leftRightKeysHandler(e);
      isHandled = true;
    }
    return isHandled;
  }
  renderCompleted(e) {
    const {
      needToRestoreFocus: needToRestoreFocus
    } = this;
    super.renderCompleted(e);
    this.unsubscribeFromGroupItemClick();
    this.subscribeToGroupItemClick();
    if (!needToRestoreFocus && this.isNeedToHiddenFocusAfterClick) {
      const $focusElement = this._getFocusedCell();
      if (null !== $focusElement && void 0 !== $focusElement && $focusElement.length) {
        hiddenFocus($focusElement.get(0));
      }
      this.isNeedToHiddenFocusAfterClick = false;
    }
  }
  canUngroupColumnByPressingKey(e) {
    return super.canUngroupColumnByPressingKey(e) || "backspace" === e.keyName || "del" === e.keyName;
  }
  getFirstFocusableVisibleIndex() {
    var _this$headerPanel3;
    const columns = null === (_this$headerPanel3 = this.headerPanel) || void 0 === _this$headerPanel3 ? void 0 : _this$headerPanel3.getColumns();
    return null !== columns && void 0 !== columns && columns.length ? 0 : -1;
  }
  init() {
    this.headerPanel = this.getView("headerPanel");
    this.groupItemClickHandlerContext = this.groupItemClickHandlerContext ?? this.groupItemClickHandler.bind(this);
    super.init();
  }
  canReorderColumn(groupColumn, direction) {
    const allowDragging = this.headerPanel.allowDragging(groupColumn);
    if (!allowDragging) {
      return false;
    }
    const groupedColumns = this._columnsController.getGroupColumns();
    return direction === Direction.Next ? groupColumn.groupIndex !== groupedColumns.length - 1 : 0 !== groupColumn.groupIndex;
  }
  ungroupAllColumns() {
    this.updateViewFocusPosition();
    super.ungroupAllColumns();
  }
}
gridCore.registerModule("groupPanelKeyboardNavigation", {
  controllers: {
    groupPanelKeyboardNavigation: GroupPanelKeyboardNavigationController
  }
});